import { computed, nextTick } from 'vue';
import {
  getAssetContainerStatusIcon,
  getAssetContainerStatusColor,
  getAssetContainerStateIcon
} from '@satellite/helpers/yardTaskUtils';
import { useYardAssetStatus } from '@satellite/composables/useYardAssetStatus';
import { useRoute, useRouter } from 'vue-router';
import { isUUID } from 'class-validator';
import { useStore } from 'vuex';
import { camelCase, get, isString } from 'lodash';
import { DateTime } from 'luxon';
import { useAuth, useEventHub, useCustomForms, useNotify } from '@/composables';
import { useYardAnalytics } from '@/composables/useYardAnalytics';
import {
  AssetVisitType,
  SpotType,
  SPOT_STATUSES_RESERVED,
  SpotStatus,
  getSpotStatus,
  LuxonDateTimeFormats,
  isSpotAssignedToAssetVisit,
  isSpotAssignedToAssetContainer,
  isSpotAssignedOnlyToAssetContainer,
  isSpotAssignedOnlyToAssetVisit,
  isAssetVisitCancelled,
  isAssetVisitDeparted,
  canUserCreateSpotAssignment,
  formatDateTimeWithMilitarySupport,
  YARD_SPOT_CODE_MAX_LENGTH,
  AppointmentStatus,
  sortSpots,
  LoadTypeDirection,
  AssetContainerEventType,
  isFeatureEnabledForWarehouse,
  WarehouseFeatureEnum,
  YardViewModeEnum,
  canUserCreateSpot,
  isUserRoleGreaterOrEqual,
  UserRole,
  isUserRoleLessOrEqual,
  getDirectionForVisitType,
  AssetVisitDirection
} from '@nova/core';
import { useI18n } from 'vue-i18n';

/** Key of the column after which custom (asset detail) columns are rendered in the list view. */
export const CUSTOM_COLUMNS_AFTER_KEY = 'spotCode';

/**
 * Returns standard columns with custom columns inserted immediately after the anchor column.
 * Used by list view (header order) and column select (dropdown order).
 */
export function placeCustomColumnsAfterAnchor(
  standardColumns,
  customColumns,
  anchorKey = CUSTOM_COLUMNS_AFTER_KEY
) {
  const anchorIndex = standardColumns.findIndex(c => c.key === anchorKey);
  if (anchorIndex === -1 || !customColumns?.length) {
    return [...standardColumns, ...(customColumns || [])];
  }
  return [
    ...standardColumns.slice(0, anchorIndex + 1),
    ...customColumns,
    ...standardColumns.slice(anchorIndex + 1)
  ];
}

export default function useYard() {
  const { t, locale } = useI18n();
  const assetStatus = useYardAssetStatus();
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const yardAnalytics = useYardAnalytics();
  const eventHub = useEventHub();
  const { getFormattedValue } = useCustomForms();
  const notify = useNotify();

  // Event payload: { assetVisit: Object, newSpotCode: string, newSpotAreaName: string }
  const SPOT_ASSIGNMENT_CHANGED = 'spot-assignment-changed';

  // Event payload: { newSpotCode: string }
  const GO_TO_SPOT = 'go-to-spot';

  // Event payload: void (signals a new check-in arrived via websocket)

  const storePath = 'state.Yard';
  const spotCardWidth = 60;
  const spotCardHeight = 170;
  const getSpotIcon = type => {
    return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
  };

  const getTruckIcon = type => {
    if (type === AssetVisitType.PickUp) {
      return 'mdi-truck-flatbed';
    }

    if (type === AssetVisitType.Drop || type === AssetVisitType.DropHook) {
      return 'mdi-truck-cargo-container';
    }

    return 'mdi-truck-outline';
  };

  const isYMSEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.YMS)
  );

  const isGateManagementEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.GateManagement)
  );

  const getArrivedAssetContainerEvent = assetContainer => {
    if (!assetContainer?.assetContainerEvents?.length) {
      return ' - ';
    }

    return assetContainer.assetContainerEvents.find(
      e => e?.eventType === AssetContainerEventType.Arrived
    );
  };

  const formatHour = value =>
    formatDateTime(
      value,
      LuxonDateTimeFormats.Extended12HrTimeAMPM,
      LuxonDateTimeFormats.Extended24HrTime
    );

  const getAssignedAssetContainerStatus = assetContainerEvents =>
    assetStatus.getDisplayStatus(assetContainerEvents);

  const isAssetContainer = asset =>
    asset && isString(asset.code) && Boolean(asset.type) && !Object.hasOwn(asset, 'visitType');

  const getAssignedSpotIcon = loadtypeDirection => {
    if (loadtypeDirection) {
      if (loadtypeDirection === LoadTypeDirection.Inbound) {
        return 'mdi-menu-up';
      }

      if (loadtypeDirection === LoadTypeDirection.Outbound) {
        return 'mdi-menu-down';
      }

      if (loadtypeDirection === LoadTypeDirection.InOut) {
        return 'mdi-menu-swap';
      }
    }

    return null;
  };

  const getLoadTypeDirectionColor = loadTypeDirection => {
    if (loadTypeDirection === LoadTypeDirection.Inbound) {
      return 'info';
    }
    if (loadTypeDirection === LoadTypeDirection.Outbound) {
      return 'warning';
    }

    return 'text--color-black';
  };

  const relativeTimeFromWarehouse = date => {
    const zone = selectedWarehouse.value.timezone;
    return DateTime.fromISO(date, { zone })
      .setLocale(locale.value)
      .toRelative({ base: DateTime.now().setZone(zone) })
      .replace('ago', '');
  };

  const goToSpotFromOtherModules = async ({ spotId, spotCode, warehouseId }) => {
    if (!spotId || !spotCode || !warehouseId) {
      throw new Error('Invalid parameters for goToSpotFromOtherModules');
    }

    return router
      .push({
        name: 'yard-warehouse',
        params: {
          warehouseId
        }
      })
      .then(() => {
        // Wait for spots and visits to load before emitting the event
        let maxChecks = 5;
        const { $emit } = useEventHub();
        const checkLoading = () => {
          if (maxChecks-- <= 0) {
            throw new Error('Timeout waiting for spots and visits to load');
          }

          if (
            !isLoadingSpots.value &&
            !isLoadingAssetVisits.value &&
            selectedAreas.value.length > 0 &&
            spots.value.length > 0
          ) {
            const spot = spots.value.find(spot => spot.id === spotId);
            selectedSpot.value = spot;

            viewMode.value = YardViewModeEnum.GRID;

            yardAnalytics.trackYardGoToSpotClicked({
              spot: { id: spotId, code: spotCode },
              entryPoint: 'Appointment Details'
            });

            $emit(GO_TO_SPOT, {
              newSpotCode: spotCode
            });
          } else {
            setTimeout(checkLoading, 400);
          }
        };
        checkLoading();
      });
  };

  const truckThemeColors = {
    blue: {
      backgroundColor: '#E4F1F7',
      borderColor: '#005AD8'
    },
    red: {
      backgroundColor: '#FAEBEE',
      borderColor: '#D40047'
    },
    grey: {
      backgroundColor: '#F2F2F2',
      borderColor: '#656565'
    }
  };

  const getTruckThemeColor = assetVisit => {
    const appointment = assetVisit?.appointment;
    const warehouseNow = DateTime.now().setZone(selectedWarehouse.value.timezone);

    if (appointment?.id) {
      if (
        appointment.status == AppointmentStatus.Cancelled ||
        appointment.status == AppointmentStatus.NoShow ||
        // If appointment end time is in the past, we should consider it as cancelled
        warehouseNow > DateTime.fromISO(appointment.end)
      ) {
        return { ...truckThemeColors.red, theme: 'red' };
      }

      // If it is an active appointment and not ended yet, show as blue
      return { ...truckThemeColors.blue, theme: 'blue' };
    }

    // When no appointment, show as grey
    return { ...truckThemeColors.grey, theme: 'grey' };
  };

  const isEditableSpotSelected = spot => {
    if (selectedSpots?.value?.length >= 1) {
      return Boolean(selectedSpots.value.find(s => s.id === spot.id)?.id);
    }

    return selectedSpot.value?.id === spot.id;
  };

  const isMilitaryTimeEnabled = computed(() =>
    store.getters['Settings/isMilitaryTimeEnabled']({
      ...selectedWarehouse.value
    })
  );

  const getContainerSpotAssignmentText = assetContainerId => {
    const spot = spots.value.find(spot =>
      spot.spotAssignments?.find(
        sa =>
          sa.assetContainerId === assetContainerId ||
          sa.assetVisit?.assetContainers?.find(ac => ac.id === assetContainerId)
      )
    );

    if (!spot) {
      return {
        spotAreaName: null,
        spotCode: null,
        spotId: null
      };
    }

    const spotArea = spotsByAreaMap.value.get(spot.spotAreaId)?.area ?? null;

    return {
      spotAreaName: spotArea?.name ?? spot.spotAreaName ?? null,
      spotCode: spot.code ?? null,
      spotId: spot.id ?? null
    };
  };

  const visitTypeToI18nKey = {
    [AssetVisitType.Live]: 'live',
    [AssetVisitType.LiveUnload]: 'live',
    [AssetVisitType.LiveLoad]: 'live',
    [AssetVisitType.Drop]: 'drop',
    [AssetVisitType.PickUp]: 'pickup',
    [AssetVisitType.DropHook]: 'dropHook'
  };

  const directionToI18nKey = {
    [AssetVisitDirection.Inbound]: 'inbound',
    [AssetVisitDirection.Outbound]: 'outbound',
    [AssetVisitDirection.InboundAndOutbound]: 'inboundAndOutbound'
  };

  const getAssetVisitTypeDisplayName = type => {
    const typeKey = visitTypeToI18nKey[type];
    if (!typeKey) return type;

    const typeName = t(`core.yard.assetVisitTypes.${typeKey}`);
    const direction = getDirectionForVisitType(type);
    if (!direction) return typeName;

    const dirKey = directionToI18nKey[direction];
    const directionName = t(`core.yard.assetVisitDirections.${dirKey}`);
    return `${typeName} · ${directionName}`;
  };

  const isLoadingSpots = computed(() => get(store, `${storePath}.isLoadingSpots`));
  const isLoadingAssetVisits = computed(() => get(store, `${storePath}.isLoadingAssetVisits`));

  const spotAreas = computed(() => get(store, `${storePath}.spotAreas`));
  const spots = computed(() => get(store, `${storePath}.spots`));
  const assetVisits = computed(() => get(store, `${storePath}.assetVisits`));
  const assetContainers = computed(() => get(store, `${storePath}.assetContainers`));

  const waitingAssetVisits = computed(() => {
    // Filter out departed/canceled but NOT assigned — assignment status is
    // determined reactively from spots.value below, not from the entity's own
    // spotAssignments (which can be stale after optimistic updates).
    const validAssetVisits = (assetVisits.value || []).filter(
      av => !isAssetVisitCancelled(av) && !isAssetVisitDeparted(av)
    );
    const assignedAssetVisitIds = new Set(
      spots.value
        .filter(s => s?.spotAssignments?.length > 0)
        .flatMap(s => [
          s.spotAssignments[0]?.assetContainerId,
          s.spotAssignments[0]?.assetVisitId,
          ...(s.spotAssignments[0]?.assetVisit?.assetContainers?.map(ac => ac.id) || [])
        ])
    );

    const notAssigned = validAssetVisits.filter(av => !assignedAssetVisitIds.has(av.id));

    return notAssigned;
  });

  // Read-write refs
  const selectedWarehouse = computed({
    get() {
      return get(store, `${storePath}.selectedWarehouse`);
    },
    set(value) {
      store.commit('Yard/setSelectedWarehouse', value || null);

      if (value?.id) {
        // Make sure all related data is fetched
        fetchAssetVisits();
        fetchSpotAreas();
        fetchSpots();

        // Update warehouseId to the url
        router.push({
          params: { warehouseId: value.id }
        });
      } else {
        // Clean up the url
        router.push({
          name: 'yard'
        });
      }
    }
  });
  const selectedAreas = computed({
    get() {
      return get(store, `${storePath}.selectedAreas`);
    },
    set(value) {
      store.commit('Yard/setSelectedAreas', value || []);
    }
  });
  const selectedSpot = computed({
    get() {
      return get(store, `${storePath}.selectedSpot`);
    },
    set(value) {
      store.commit('Yard/setSelectedSpot', value || {});
      if (value?.id) {
        store
          .dispatch('Yard/fetchSpot', value.id)
          .then(spot => store.commit('Yard/setSelectedSpot', spot));
        selectedSpots.value = [];

        nextTick(() => {
          const element = document.getElementById(value.id);

          if (element) {
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }, 500);
          }
        });
      }
    }
  });
  const selectedAssetVisit = computed({
    get() {
      return get(store, `${storePath}.selectedAssetVisit`);
    },
    set(value) {
      store.commit('Yard/setSelectedAssetVisit', value || null);
    }
  });
  const isAssetVisitChatVisible = computed({
    get() {
      return get(store, `${storePath}.isAssetVisitChatVisible`);
    },
    set(value) {
      store.commit('Yard/setIsAssetVisitChatVisible', value);
    }
  });
  const selectedSpots = computed({
    get() {
      return get(store, `${storePath}.selectedSpots`);
    },
    set(value) {
      store.commit('Yard/setSelectedSpots', value || []);
      if (value?.length > 0) {
        selectedSpot.value = {};
      }
    }
  });
  const viewMode = computed({
    get() {
      return get(store, `${storePath}.viewMode`);
    },
    set(value) {
      store.dispatch('Yard/setViewMode', { value });
    }
  });

  // Route refs
  const defaultWarehouseId = computed(() =>
    isUUID(route.params?.warehouseId) ? String(route.params.warehouseId) : null
  );
  const defaultSpotAreaId = computed(() =>
    isUUID(route.params?.spotAreaId) ? String(route.params.spotAreaId) : null
  );

  const dropHandled = computed(() => get(store, `${storePath}.dropHandled`));
  const setDropHandled = value => store.commit('Yard/setDropHandled', Boolean(value));
  const dragOriginSpotIdRef = computed(() => get(store, `${storePath}.dragOriginSpotId`));
  const setDragOriginSpotId = value =>
    store.commit('Yard/setDragOriginSpotId', value == null ? null : String(value));

  const auth = useAuth();

  const cannotCreateSpotAssignment = computed(() => {
    const user = auth.meRef.value;
    return !canUserCreateSpotAssignment(user);
  });

  const canManageSpotsAndAreas = computed(() => {
    const user = auth.meRef.value;
    return canUserCreateSpot(user);
  });

  const canEditAssetForms = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR);
  });

  const canEditAssetContainerData = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.ATTENDANT);
  });

  const isReadonlyAccess = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleLessOrEqual(user.role, UserRole.SPECTATOR);
  });

  // Actions
  const fakeCheckIn = data => store.dispatch('Yard/fakeCheckIn', data);
  const fakeCheckInUnplanned = data => store.dispatch('Yard/fakeCheckInUnplanned', data);
  const fetchPreviousYardData = () => store.dispatch('Yard/fetchPreviousYardData');
  const persistSelectedWarehouse = warehouseId =>
    store.dispatch('Yard/persistSelectedWarehouse', warehouseId);
  const detachAssetVisit = (
    assetVisitId,
    assetVisitAction,
    assetContainerActions,
    reassignData = null
  ) =>
    store.dispatch('Yard/detachAssetVisit', {
      assetVisitId,
      assetVisitAction,
      assetContainerActions,
      reassignData
    });
  const attachAssetVisit = (assetVisitId, assetContainerActions) =>
    store.dispatch('Yard/attachAssetVisit', { assetVisitId, assetContainerActions });
  const updateAssetContainer = (assetContainerId, data) =>
    store.dispatch('Yard/updateAssetContainer', { assetContainerId, data });
  const updateAssetContainerStatus = (assetContainerId, eventType) =>
    store.dispatch('Yard/updateAssetContainerStatus', { assetContainerId, eventType });
  const updateAssetVisitStatus = (assetVisitId, eventType) =>
    store.dispatch('Yard/updateAssetVisitStatus', { assetVisitId, eventType });
  const getAppointmentById = appointmentId =>
    store.dispatch('Appointments/getAppointmentById', appointmentId);
  const fetchAssetVisits = () => store.dispatch('Yard/fetchAssetVisits');
  const fetchAndUpsertAssetContainer = id =>
    store.dispatch('Yard/fetchAndUpsertAssetContainer', id);
  const fetchAssetContainers = () => store.dispatch('Yard/fetchAssetContainers');
  const fetchSpots = () => store.dispatch('Yard/fetchSpots');
  const fetchSpotAreas = () => store.dispatch('Yard/fetchSpotAreas');
  const fetchSpot = id => store.dispatch('Yard/fetchSpot', id);
  const createSpotArea = data => store.dispatch('Yard/createSpotArea', data);
  const updateSpotArea = (id, data) => store.dispatch('Yard/updateSpotArea', { id, data });
  const deleteSpotArea = id => store.dispatch('Yard/deleteSpotArea', id);
  const updateSpot = (id, data) => store.dispatch('Yard/updateSpot', { id, data });
  const createSpot = data => store.dispatch('Yard/createSpot', data);
  const createManySpot = data => store.dispatch('Yard/createManySpot', data);
  const closeSpot = (id, data) => store.dispatch('Yard/closeSpot', { id, data });
  const openSpot = (id, data) => store.dispatch('Yard/openSpot', { id, data });
  const deleteSpot = id => store.dispatch('Yard/deleteSpot', id);
  const deleteManySpot = ids => store.dispatch('Yard/deleteManySpot', ids);
  const createSpotAssignment = data => store.dispatch('Yard/createSpotAssignment', data);
  const deleteSpotAssignment = id => store.dispatch('Yard/deleteSpotAssignment', id);
  const departSpotAssignment = (id, assetType) =>
    store.dispatch('Yard/departSpotAssignment', { id, assetType });
  const updateSpotAssignment = (id, data) =>
    store.dispatch('Yard/updateSpotAssignment', { id, data });
  const reassignSpotAssignment = (id, data) =>
    store.dispatch('Yard/reassignSpotAssignment', { id, data });

  /**
   * Executes spot reassignment, emits SPOT_ASSIGNMENT_CHANGED and tracks Mixpanel.
   * On failure (invalid or missing spotAssignment), notifies the user and returns null.
   * @param {string} spotAssignmentId - ID of the spot assignment to reassign
   * @param {Object} oldSpot - Current spot object (with spotAssignments)
   * @param {Object} newSpot - Target spot object
   * @param {{ reason?: string, entryPoint?: string }} options - reason and entryPoint for the action
   * @returns {Promise<Object|null>} The spotAssignment from the API, or null if invalid (caller notified).
   */
  const executeReassignSpotSelection = async (spotAssignmentId, oldSpot, newSpot, options = {}) => {
    const { reason = 'Drag and Drop', entryPoint = 'Drag and Drop' } = options;

    const spotAssignment = await reassignSpotAssignment(spotAssignmentId, {
      spotId: newSpot.id,
      oldSpotId: oldSpot.id,
      reason
    });

    if (!spotAssignment?.id) {
      notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
      return null;
    }

    const isReassigningAssetVisit = isSpotAssignedToAssetVisit(oldSpot);

    eventHub.$emit(SPOT_ASSIGNMENT_CHANGED, {
      assetVisit: isReassigningAssetVisit
        ? (spotAssignment.assetVisit ?? spotAssignment.assetContainer)
        : spotAssignment.assetContainer,
      newSpotCode: spotAssignment.spot.code,
      newSpotAreaName: spotAssignment.spot.spotArea.name
    });

    yardAnalytics.trackYardAssetReassignedToSpot(
      {
        spot: {
          ...newSpot,
          spotAssignments: oldSpot.spotAssignments?.[0] ? { ...oldSpot.spotAssignments[0] } : {}
        },
        entryPoint,
        assetVisit:
          oldSpot.spotAssignments?.[0]?.assetVisit ?? oldSpot.spotAssignments?.[0]?.assetContainer
      },
      {
        old_spot_id: oldSpot.id,
        old_spot_code: oldSpot.code
      }
    );

    return spotAssignment;
  };

  /**
   * Assigns an asset (assetVisit or assetContainer) to a spot. Creates or updates spot assignment,
   * emits SPOT_ASSIGNMENT_CHANGED, tracks Mixpanel and updates selectedSpotRef.
   * On failure (assignment create/update returns no id), notifies the user and returns null.
   * @param {Object} assetVisitOrContainer - The asset visit or asset container to assign
   * @param {string} spotId - ID of the target spot
   * @param {{ clearSelectedAssetVisit?: boolean, entryPoint?: string }} [options] - clearSelectedAssetVisit: clear selectedAssetVisitRef on success (e.g. detail panel); entryPoint: for Mixpanel (default 'Select from List')
   * @returns {Promise<Object|null>} The spotAssignment from the API, or null if invalid (caller notified on API failure)
   */
  const assignSpotToAsset = async (assetVisitOrContainer, spotId, options = {}) => {
    const { clearSelectedAssetVisit = false, entryPoint = 'Select from List' } = options;

    if (!assetVisitOrContainer?.id) return null;

    const spot = spots.value.find(s => s.id === spotId);
    if (!spot?.id) return null;

    if (isSpotReserved(spot)) {
      notify(t('yard.messages.thisSpotIsReservedForAnotherTask'), 'warning');
      return null;
    }

    if (isSpotClosed(spot)) {
      notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
      return null;
    }

    const isAssetContainerSelected = isAssetContainer(assetVisitOrContainer);
    let spotAssignment = null;

    const assignmentPayload = {
      [isAssetContainerSelected ? 'assetContainerId' : 'assetVisitId']: assetVisitOrContainer.id,
      spotId: spot.id
    };

    if (isSpotOpen(spot)) {
      spotAssignment = await createSpotAssignment(assignmentPayload);
    } else {
      const isAssetContainerAssignedToVisitOnly =
        isAssetContainerSelected && isSpotAssignedOnlyToAssetVisit(spot);
      const isVisitAssignedToContainerOnly =
        isSpotAssignedOnlyToAssetContainer(spot) && !isAssetContainerSelected;
      const isSpotFullyOccupied =
        isSpotAssignedToAssetVisit(spot) && isSpotAssignedToAssetContainer(spot);

      if (
        !isSpotFullyOccupied &&
        (isAssetContainerAssignedToVisitOnly || isVisitAssignedToContainerOnly)
      ) {
        const existingAssignmentId = spot.spotAssignments?.[0]?.id;
        if (existingAssignmentId) {
          spotAssignment = await updateSpotAssignment(existingAssignmentId, assignmentPayload);
        } else {
          spotAssignment = await createSpotAssignment(assignmentPayload);
        }
      } else {
        notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
        return null;
      }
    }

    if (!spotAssignment?.id) {
      notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
      return null;
    }

    eventHub.$emit(SPOT_ASSIGNMENT_CHANGED, {
      assetVisit: assetVisitOrContainer,
      newSpotCode: spot.code,
      newSpotAreaName: spotAreas.value.find(area => area.id === spot.spotAreaId)?.name
    });

    yardAnalytics.trackYardAssetAssignedToSpot({
      spot,
      assetVisit: assetVisitOrContainer,
      entryPoint
    });

    selectedSpot.value = spot;
    if (clearSelectedAssetVisit) {
      selectedAssetVisit.value = null;
    }

    return spotAssignment;
  };

  const messageDriver = (assetVisitId, content) =>
    store.dispatch('Yard/messageDriver', { assetVisitId, content });
  const setIsLoadingSpots = value => store.commit('Yard/setIsLoadingSpots', value);
  const setIsLoadingAssetVisits = value => store.commit('Yard/setIsLoadingAssetVisits', value);

  // Subspace
  const showUpdateDataFromSubspace = (event, collection) => {
    return (
      collection?.value?.find(item => item.id === event.id) ||
      event.warehouseId === selectedWarehouse.value?.id
    );
  };
  const updateSpotOnSpotList = spot => {
    if (!spot?.id) {
      return;
    }

    if (spots.value.map(s => s.id).includes(spot.id)) {
      store.commit(
        'Yard/setSpots',
        spots.value.map(s => (s.id === spot.id ? spot : s))
      );
    } else {
      store.commit('Yard/setSpots', [...spots.value, spot]);
    }

    if (selectedSpot.value?.id === spot.id) {
      store.commit('Yard/setSelectedSpot', spot);
    }
  };
  const handleSpotSubspaceEvent = async event => {
    if (event.debounce?.count > 0) {
      return fetchSpots().then(() => {
        if (event.debounce.ids?.includes(selectedSpot.value?.id)) {
          store.commit('Yard/setSelectedSpot', {});
        }
      });
    }

    if (showUpdateDataFromSubspace(event, spots)) {
      if (event.action === 'delete') {
        store.commit(
          'Yard/setSpots',
          spots.value.filter(s => s.id !== event.id)
        );

        if (selectedSpot.value?.id === event.id) {
          store.commit('Yard/setSelectedSpot', {});
        }
        return;
      }
      return fetchSpot(event.id).then(updateSpotOnSpotList);
    }
  };

  const validateSpotCodeInput = value => {
    if (
      value?.length < 1 ||
      value?.length > YARD_SPOT_CODE_MAX_LENGTH ||
      !/^[a-zA-Z0-9]*$/.test(value)
    ) {
      return `Code must be between 1 and ${YARD_SPOT_CODE_MAX_LENGTH} characters and contains only letters and numbers (a-z,  0-9)`;
    }
    return true;
  };

  const validateSpotTypeInput = value => {
    if (!value) {
      return 'Spot type is required';
    }

    if (!Object.values(SpotType).includes(value)) {
      return `Spot type is not valid, valid choices are ${Object.values(SpotType).join(',')}`;
    }
    return true;
  };

  const formatDateTime = (date, amPmFormat, militaryFormat) =>
    formatDateTimeWithMilitarySupport(
      date,
      selectedWarehouse.value?.timezone,
      amPmFormat,
      isMilitaryTimeEnabled.value,
      militaryFormat,
      locale.value
    );

  const getTranslatedAssetContainerType = type => {
    return t(`core.yard.assetContainerTypes.${camelCase(type)}`);
  };

  const getTranslatedSpotStatus = spot => t(`core.yard.spotStatuses.${camelCase(spot.status)}`);

  const spotsByAreaMap = computed(() => {
    const spotsByArea = new Map();

    spotAreas.value.forEach(area => {
      spotsByArea.set(area.id, {
        area,
        spots: []
      });
    });

    spots.value.forEach(spot => {
      if (!spot.isActive) {
        return;
      }

      const areaData = spotsByArea.get(spot.spotAreaId);
      if (areaData) {
        areaData.spots.push(spot);
      } else {
        console.warn('Could not find area', spot.spotAreaId, { spot: JSON.stringify(spot) });
      }
    });

    return spotsByArea;
  });

  const getSpotsByArea = areaId => {
    return spotsByAreaMap.value.get(areaId)?.spots || [];
  };

  const isSpotOpen = spot => spot.status === SpotStatus.OPEN;
  const isSpotClosed = spot => spot.status === SpotStatus.CLOSED;
  const isSpotAssigned = spot => spot.status === SpotStatus.ASSIGNED;
  const isSpotReserved = spot => SPOT_STATUSES_RESERVED.includes(spot.status);

  /**
   * TODO: evaluate if we need to increase the granularity of this getter
   * to return if the spot is the origin or destination of an active move task,
   * or the actual active move task itself.
   *
   * If we do, we need to update the `useYardSpotStatus` composable to use the new getter,
   * or remove it altogether.
   */
  const isSpotInActiveMoveTask = spotId => {
    return store.getters['Yard/isSpotInActiveMoveTask'](spotId);
  };

  const hasAssignedAssetContainer = spot => {
    const toArray = value => (Array.isArray(value) ? value : []);
    const spotAssignments = toArray(spot?.spotAssignments);

    return spotAssignments.some(spotAssignment => {
      if (spotAssignment.assetContainer?.id) {
        return true;
      }

      if (toArray(spotAssignment.assetVisit?.assetContainers).some(ac => ac.id)) {
        return true;
      }
      return false;
    });
  };

  const doesSpotHaveLockedAsset = spot => {
    /**
     * We check this because, the origin spot for a move task - which is causes
     * an asset to be "locked" - is currently being set as closed (i.e., isActive = false).
     */
    if (!isSpotClosed(spot)) {
      return false;
    }

    return hasAssignedAssetContainer(spot);
  };

  return {
    // Read-Write Refs
    selectedWarehouseRef: selectedWarehouse,
    selectedAreasRef: selectedAreas,
    selectedSpotRef: selectedSpot,
    selectedAssetVisitRef: selectedAssetVisit,
    selectedSpotsRef: selectedSpots,
    viewModeRef: viewMode,
    isAssetVisitChatVisibleRef: isAssetVisitChatVisible,
    // Read-Only Refs
    spotAreasRef: spotAreas,
    spotsRef: spots,
    assetContainersRef: assetContainers,
    isLoadingSpotsRef: isLoadingSpots,
    isLoadingAssetVisitsRef: isLoadingAssetVisits,
    defaultWarehouseIdRef: defaultWarehouseId,
    defaultSpotAreaIdRef: defaultSpotAreaId,
    waitingAssetVisitsRef: waitingAssetVisits,
    // Methods
    fakeCheckIn,
    fakeCheckInUnplanned,
    attachAssetVisit,
    detachAssetVisit,
    updateAssetContainerStatus,
    updateAssetVisitStatus,
    getAppointmentById,
    fetchSpots,
    fetchSpotAreas,
    fetchAssetVisits,
    fetchAndUpsertAssetContainer,
    fetchAssetContainers,
    createSpotArea,
    updateSpotArea,
    deleteSpotArea,
    getArrivedAssetContainerEvent,
    handleSpotSubspaceEvent,
    createSpot,
    createManySpot,
    closeSpot,
    openSpot,
    deleteSpot,
    deleteManySpot,
    fetchSpot,
    updateSpot,
    messageDriver,
    fetchPreviousYardData,
    createSpotAssignment,
    deleteSpotAssignment,
    departSpotAssignment,
    reassignSpotAssignment,
    executeReassignSpotSelection,
    assignSpotToAsset,
    updateSpotAssignment,
    dropHandled,
    setDropHandled,
    dragOriginSpotIdRef,
    setDragOriginSpotId,
    validateSpotCodeInput,
    validateSpotTypeInput,
    getAssetVisitTypeDisplayName,
    formatDateTime,
    getTruckThemeColor,
    setIsLoadingSpots,
    isEditableSpotSelected,
    isReadonlyAccess,
    sortSpots,
    relativeTimeFromWarehouse,
    getAssignedAssetContainerStatus,
    setIsLoadingAssetVisits,
    updateAssetContainer,
    isAssetContainer,
    updateSpotOnSpotList,
    isSpotOpen,
    isSpotClosed,
    isSpotAssigned,
    isSpotReserved,
    isSpotInActiveMoveTask,
    hasAssetVisitOnSpot: spot => isSpotAssignedToAssetVisit(spot),
    hasAssetContainerOnSpot: spot => isSpotAssignedToAssetContainer(spot),
    hasAssetVisitOnlyOnSpot: spot => isSpotAssignedOnlyToAssetVisit(spot),
    hasAssetContainerOnlyOnSpot: spot => isSpotAssignedOnlyToAssetContainer(spot),
    // Basic Data
    spotCardHeight,
    spotCardWidth,
    spotType: SpotType,
    spotStatus: SpotStatus,
    persistSelectedWarehouse,
    getAssignedSpotIcon,
    getAssetContainerStateIcon,
    getAssetContainerStatusIcon,
    getAssetContainerStatusColor,
    getSpotIcon,
    getTruckIcon,
    getLoadTypeDirectionColor,
    getContainerSpotAssignmentText,
    isYMSEnabledForSelectedWarehouse,
    isGateManagementEnabledForSelectedWarehouse,
    doesSpotHaveLockedAsset,
    cannotCreateSpotAssignmentRef: cannotCreateSpotAssignment,
    canManageSpotsAndAreasRef: canManageSpotsAndAreas,
    canEditAssetFormsRef: canEditAssetForms,
    canEditAssetContainerDataRef: canEditAssetContainerData,
    getSpotStatus,
    isMilitaryTimeEnabled,
    goToSpotFromOtherModules,
    truckThemeColors,
    YARD_SPOT_CODE_MAX_LENGTH,
    SPOT_ASSIGNMENT_CHANGED,
    GO_TO_SPOT,
    getTranslatedAssetContainerType,
    getTranslatedSpotStatus,
    spotsByAreaMap,
    getSpotsByArea
  };
}
